import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  Tab,
  Form,
  Button,
  Image,
  Card,
  Modal,
  Table,
  OverlayTrigger,
  Tooltip,
  Dropdown,
  Alert,
} from "react-bootstrap";
import UserManagement from "./UserManagement";
import CreditDashboard from "./CreditDashboard";
import CompanyManagement from "./CompanyManagement";
import searchIcon from "../assets/images/icons/search.png";
import "../../src/css/AccountManagement.css";
import creditManagementIcon from "../assets/images/icons/custom-domain-icon.png";
import Analytics from "./Analytics";
import { jwtDecode } from "jwt-decode";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import "react-toastify/dist/ReactToastify.css";
import companyImage from "../assets/images/icons/CompanyIcon.png";
import { accountService } from "../services/accountService";
import { userService } from "../services/userService";
import { creditService } from "../services/creditService";
import { domainService } from "../services/domainService";

import { faPencil } from "@fortawesome/free-solid-svg-icons";
import Tabs from "react-bootstrap/Tabs";
import { FaTrash, FaStar, FaEllipsisV } from "react-icons/fa";
import { IoIosArrowRoundBack } from "react-icons/io";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import CompanySidebar from "../components/CompanySidebar";
import { authService } from '../services/authService';

const formControlStyle = {
  padding: "8px 12px",
  borderRadius: "6px",
  border: "1px solid #D0D5DD",
  fontSize: "14px",
};

const labelStyle = {
  fontSize: "14px",
  fontWeight: "500",
  color: "#344054",
  marginBottom: "6px",
};

const AccountManagement = () => {
  const [activeTab, setActiveTab] = useState("account-dashboard");
  const [accountDetails, setAccountDetails] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDetails, setEditedDetails] = useState({});
  const [newDomain, setNewDomain] = useState("");
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [domainsList, setDomainsList] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [senderIdsList, setSenderIdsList] = useState([]);
  const [showSenderIdModal, setShowSenderIdModal] = useState(false);
  const [newSenderId, setNewSenderId] = useState("");
  const [domainError, setDomainError] = useState("");
  const [senderIdError, setSenderIdError] = useState("");
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({
    accountName: "",
    domains: [],
    defaultDomain: "",
    senderIds: [],
    isActive: true,
  });
  const [newCompanyErrors, setNewCompanyErrors] = useState({
    accountName: "",
    domains: "",
    defaultDomain: "",
    senderIds: "",
  });

  const normalizeList = (input) => {
    const arr = Array.isArray(input)
      ? input
      : (input || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
    return Array.from(new Set(arr));
  };

  const isValidUrl = (url) => {
    try {
      const u = new URL(url);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  const validateAccountName = (name) => {
    const value = (name || "").trim();
    if (!value) return "Account name is required";
    if (value.length < 3) return "Account name must be at least 3 characters";
    return "";
  };

  const validateDomains = (domains) => {
    const list = normalizeList(domains);
    if (list.length === 0) return "At least one domain is required";
    const invalid = list.find((d) => !isValidUrl(d));
    if (invalid) return `Invalid domain URL: ${invalid}`;
    return "";
  };

  const validateDefaultDomain = (defaultDomain, domains) => {
    const value = (defaultDomain || "").trim();
    const list = normalizeList(domains);
    if (!value) return "Default domain is required";
    if (!isValidUrl(value)) return "Default domain must be a valid URL";
    if (!list.includes(value))
      return "Default domain must be one of the domains";
    return "";
  };

  const validateSenderIds = (senderIds) => {
    const list = normalizeList(senderIds);
    if (list.length === 0) return "Enter at least one sender ID";
    const pattern = /^[A-Za-z0-9]{3,20}$/;
    const invalid = list.find((id) => !pattern.test(id));
    if (invalid)
      return "Sender IDs must be 3-20 chars (letters and numbers only, no symbols)";
    return "";
  };
  // Add User modal validation helpers
  const validateUsername = (username) => {
    const value = (username || "").trim();
    if (!value) return "Username is required";
    if (value.length > 20) return "Username must not exceed 20 characters";
    const pattern = /^[A-Za-z0-9._-]{3,20}$/;
    if (!pattern.test(value))
      return "Username must be 3-20 chars (letters, numbers, . _ -)";
    return "";
  };

  const validatePassword = (password) => {
    const value = (password || "").trim();
    if (!value) return "Password is required";
    if (value.length < 8) return "Password must be at least 8 characters";
    const hasLetter = /[A-Za-z]/.test(value);
    const hasNumber = /\d/.test(value);
    if (!hasLetter || !hasNumber)
      return "Password must include letters and numbers";
    return "";
  };

  const validateEmail = (email) => {
    const value = (email || "").trim();
    if (!value) return "Email is required";
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!pattern.test(value)) return "Enter a valid email address";
    return "";
  };

  const validateContactNo = (contactNo) => {
    const value = (contactNo || "").trim();
    if (!value) return "Contact number is required";
    const pattern = /^\d{10}$/;
    if (!pattern.test(value)) return "Enter a valid 10-digit phone number";
    return "";
  };

  const validateAccountId = (accountId) => {
    const value = (accountId || "").toString();
    if (!value) return "Company selection is required";
    return "";
  };

  const validateRole = (role) => {
    const value = (role || "").trim();
    if (!value) return "Role is required";
    return "";
  };
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    email: "",
    contactNo: "",
    accountId: "",
    roles: ["USER"],
  });
  const [newUserErrors, setNewUserErrors] = useState({
    username: "",
    password: "",
    email: "",
    contactNo: "",
    accountId: "",
    role: "",
  });
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showUpdateCreditModal, setShowUpdateCreditModal] = useState(false);
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [newUserData, setNewUserData] = useState({
    companyId: "",
    accountId: "",
    availableCredits: "",
    creditType: "SHORTENING",
  });
  const [updateCreditData, setUpdateCreditData] = useState({
    accountId: "",
    transactionType: "CREDIT",
    newCredit: 0,
    active: true,
    performedBy: null,
    creditType: "SHORTENING",
  });
  const [userSub, setUserSub] = useState("");
  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [users, setUsers] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyUserCounts, setCompanyUserCounts] = useState({});
  const [companyLogos, setCompanyLogos] = useState({});
  const [defaultDomain, setDefaultDomain] = useState("");
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const tabContainerRef = useRef(null);

  // Inline validation helpers for editing fields
  const MAX_FIELD_LEN = 20;
  const accountNameLen = (editedDetails.accountName || "").length;
  const languageLen = (editedDetails.language || "").length;
  const locationLen = (editedDetails.location || "").length;
  const accountNameError =
    isEditing && (editedDetails.accountName || "").trim().length === 0
      ? "Account Name is required"
      : null;
  const isFormInvalid = !!accountNameError;
  const [selectedProtocol, setSelectedProtocol] = useState("http://");

  const handleTabSelect = (tab) => {
    setActiveTab(tab);
  };

  const fetchCompanyLogo = async (fileName) => {
    if (!fileName) {
      return null;
    }

    try {
      const blob = await accountService.downloadLogo(fileName);

      if (blob && blob.size > 0) {
        // Ensure we have a proper MIME type for the blob
        let mimeType = blob.type;
        if (!mimeType || mimeType === "application/octet-stream") {
          // If no MIME type is detected, assume it's a PNG image
          mimeType = "image/png";
        }

        // Convert blob to base64 data URL with proper MIME type
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            let dataUrl = reader.result;

            // Ensure the data URL has the correct format: data:image/png;base64,...
            if (dataUrl && !dataUrl.startsWith("data:image/")) {
              // If the data URL doesn't have the proper format, reconstruct it
              const base64Data = dataUrl.split(",")[1] || dataUrl;
              dataUrl = `data:${mimeType};base64,${base64Data}`;
            }

            resolve(dataUrl);
          };
          reader.onerror = () => {
            console.error(
              "Error reading blob as data URL in AccountManagement"
            );
            reject(reader.error);
          };
          reader.readAsDataURL(blob);
        });
      }
      return null;
    } catch (error) {
      console.error("Error fetching company logo in AccountManagement:", error);
      return null;
    }
  };

  useEffect(() => {
    const checkUserRoleAndFetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const decoded = jwtDecode(token);
          const selectedCompanyId = decoded.accountId;
          const role = decoded.roles[0];
          setUserRole(role);

          if (role === "ADMIN") {
            // Set the selectedCompanyId state for ADMIN users
            setSelectedCompanyId(selectedCompanyId);

            const response = await accountService.getAccountConfigById(
              selectedCompanyId
            );
            setAccountDetails(response);

            if (response.logo) {
              const logoUrl = await fetchCompanyLogo(response.logo);
              setCompanyLogos({ [selectedCompanyId]: logoUrl });
            }
          } else {
            const response = await accountService.getAccountConfigs();
            if (response && response.length > 0) {
              const companiesData = response;
              console.log("Fetched companies:", companiesData);
              setCompanies(companiesData);
              setSelectedCompanyId(companiesData[0].id);

              const logoPromises = companiesData.map(async (company) => {
                if (company.logo) {
                  const logoUrl = await fetchCompanyLogo(company.logo);
                  return [company.id, logoUrl];
                }
                return [company.id, null];
              });

              const logos = Object.fromEntries(await Promise.all(logoPromises));
              setCompanyLogos(logos);
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    checkUserRoleAndFetchData();
  }, []);

  useEffect(() => {
    if (accountDetails?.id) {
      fetchDomains();
      fetchSenderIds();
    }
  }, [accountDetails]);

  useEffect(() => {
    const checkUserRole = () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const decodedToken = jwtDecode(token);
          setIsSuperAdmin(decodedToken.roles.includes("SUPERADMIN"));
          setUserSub(decodedToken.sub);
          setToken(token);
        }
      } catch (err) {
        console.error("Error decoding token:", err);
        setIsSuperAdmin(false);
      }
    };
    checkUserRole();
  }, []);

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem("token");

      let logoFileName = null;
      if (editedDetails.logo instanceof File) {
        const formData = new FormData();
        formData.append("file", editedDetails.logo);

        const uploadResponse = await accountService.uploadFile(formData);

        if (uploadResponse.success) {
          logoFileName = uploadResponse.data;

          const downloadResponse = await accountService.downloadLogo(
            logoFileName
          );

          if (downloadResponse) {
            const base64Image = `data:image/png;base64,${downloadResponse}`;
            setLogoPreview(base64Image);
          }
        }
      }

      let payload = {
        id: accountDetails.id,
        accountName: editedDetails.accountName,
        language: editedDetails.language || "",
        location: editedDetails.location || "",
        isActive: editedDetails.isActive,
        logo: logoFileName || accountDetails.logo,
        defaultDomain:
          editedDetails.defaultDomain || accountDetails.defaultDomain,
        ...(userRole === "SUPERADMIN" && {
          urlTracking:
            editedDetails.urlTracking !== undefined
              ? editedDetails.urlTracking
              : accountDetails.urlTracking || false,
          individualTracking:
            editedDetails.individualTracking !== undefined
              ? editedDetails.individualTracking
              : accountDetails.individualTracking || false,
        }),
      };

      if (userRole !== "ADMIN") {
        payload = {
          ...payload,
          domains: domainsList.map((domain) => domain.domain),
          defaultDomain:
            editedDetails.defaultDomain || accountDetails.defaultDomain,
          senderIds: senderIdsList.map((sender) => sender.senderId),
        };
      }

      await accountService.updateAccountConfig(payload);

      const response = await accountService.getAccountConfigById(
        selectedCompanyId
      );
      setAccountDetails(response);

      if (userRole !== "ADMIN") {
        await fetchSenderIds();
      }

      await fetchDomains();
      await fetchSenderIds();

      setIsEditing(false);
      toast.success("Account details updated successfully");
    } catch (error) {
      console.error("Error updating account:", error);
      toast.error("Failed to update account details");
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAddDomain = async () => {
    try {
      const fullDomain = selectedProtocol + newDomain;

      // Validate domain length (50 char limit)
      if (!newDomain.trim()) {
        setDomainError("Domain is required");
        return;
      }

      if (fullDomain.length > 50) {
        setDomainError("Domain must not exceed 50 characters");
        return;
      }

      setDomainError("");
      await accountService.addDomainToAccount(accountDetails.id, fullDomain);

      await fetchDomains();
      setNewDomain("");
      setSelectedProtocol("http://"); // Reset protocol
      setShowDomainModal(false);
      toast.success("Domain added successfully");
    } catch (error) {
      console.error("Error adding domain:", error);
      setDomainError(error.response?.data?.message || "Failed to add domain");
    }
  };

  const handleSetDefaultDomain = async (domainObj) => {
    try {
      await domainService.setDefaultDomain(selectedCompanyId, domainObj.id);

      const response = await accountService.getAccountConfigById(
        selectedCompanyId
      );
      setAccountDetails(response);
      toast.success("Default domain updated successfully");
    } catch (error) {
      console.error("Error setting default domain:", error);
      toast.error("Failed to set default domain");
    }
  };

  const handleVerifyDomainNow = async (domainObj) => {
    try {
      const result = await domainService.checkDomain(selectedCompanyId, domainObj.domain);
      await fetchDomains();
      if (result && result.message) {
        if (result.assigned) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } else {
        toast.success("Domain verification initiated");
      }
    } catch (error) {
      console.error("Error verifying domain:", error);
      toast.error(error.response?.data?.message || "Failed to verify domain");
    }
  };

  const handleDeleteClick = (domainObj) => {
    setItemToDelete({
      id: domainObj.id,
      type: "domain",
      name: domainObj.domain,
    });
    setShowDeleteConfirmationModal(true);
  };

  const handleSenderIdDeleteClick = (senderIdObj) => {
    setItemToDelete({
      id: senderIdObj.id,
      type: "senderId",
      name: senderIdObj.senderId,
    });
    setShowDeleteConfirmationModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (itemToDelete.type === "domain") {
        await accountService.deleteDomain(itemToDelete.id);
        await fetchDomains();
        toast.success("Domain deleted successfully");
      } else if (itemToDelete.type === "senderId") {
        await accountService.deleteSenderId(itemToDelete.id);
        await fetchSenderIds();
        toast.success("Sender ID deleted successfully");
      }
      setShowDeleteConfirmationModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(`Failed to delete ${itemToDelete.type}`);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await accountService.getDomainsByAccount(
        accountDetails.id
      );
      setDomainsList(response);
    } catch (error) {
      console.error("Error fetching domains:", error);
      toast.error("Failed to fetch domains");
      setDomainsList([]);
    }
  };

  const fetchSenderIds = async () => {
    try {
      const response = await accountService.getSenderIdsByAccount(
        accountDetails.id
      );
      setSenderIdsList(response);
    } catch (error) {
      console.error("Error fetching sender IDs:", error);
      toast.error("Failed to fetch sender IDs");
      setSenderIdsList([]);
    }
  };

  const handleAddSenderId = async () => {
    try {
      // Validate sender ID (3-20 chars, alphanumeric only, no symbols)
      if (!newSenderId.trim()) {
        setSenderIdError("Sender ID is required");
        return;
      }

      const pattern = /^[A-Za-z0-9]{3,20}$/;
      if (!pattern.test(newSenderId)) {
        setSenderIdError(
          "Sender ID must be 3-20 characters (letters and numbers only, no symbols)"
        );
        return;
      }

      setSenderIdError("");
      await accountService.addSenderIdToAccount(accountDetails.id, newSenderId);

      await fetchSenderIds();
      setNewSenderId("");
      setShowSenderIdModal(false);
      toast.success("Sender ID added successfully");
    } catch (error) {
      console.error("Error adding sender ID:", error);
      setSenderIdError(
        error.response?.data?.message || "Failed to add sender ID"
      );
    }
  };

  const handleDeleteSenderId = async (senderId) => {
    try {
      await accountService.deleteSenderId(senderId);

      await fetchSenderIds();
      toast.success("Sender ID deleted successfully");
    } catch (error) {
      console.error("Error deleting sender ID:", error);
      toast.error("Failed to delete sender ID");
    }
  };

  const handleCreateCompany = async () => {
    try {
      const normalizedDomains = normalizeList(newCompanyData.domains);
      const normalizedSenderIds = normalizeList(newCompanyData.senderIds);

      const errors = {
        accountName: validateAccountName(newCompanyData.accountName),
        domains: validateDomains(normalizedDomains),
        defaultDomain: validateDefaultDomain(
          newCompanyData.defaultDomain,
          normalizedDomains
        ),
        senderIds: validateSenderIds(normalizedSenderIds),
      };
      setNewCompanyErrors(errors);
      const hasErrors = Object.values(errors).some((e) => !!e);
      if (hasErrors) {
        toast.error("Please fix the form errors before submitting");
        return;
      }

      const formattedData = {
        ...newCompanyData,
        domains: normalizedDomains,
        senderIds: normalizedSenderIds,
      };

      const response = await accountService.createAccount(formattedData);

      if (response) {
        setCompanies((prevCompanies) => [...prevCompanies, response]);

        try {
          const companiesResponse = await accountService.getAccountConfigs();

          if (companiesResponse) {
            setCompanies(companiesResponse);
          }

          setShowCreateCompanyModal(false);
          setNewCompanyData({
            accountName: "",
            domains: [],
            defaultDomain: "",
            senderIds: [],
            isActive: true,
          });
          setNewCompanyErrors({
            accountName: "",
            domains: "",
            defaultDomain: "",
            senderIds: "",
          });

          toast.success("Company added successfully");

          const freshCompaniesData = await accountService.getAccountConfigs();

          if (freshCompaniesData) {
            setCompanies(freshCompaniesData);

            if (freshCompaniesData.length === 1) {
              setSelectedCompanyId(freshCompaniesData[0].id);
            }
          }

          if (selectedCompanyId) {
            const companyDetailsResponse =
              await accountService.getAccountConfigById(selectedCompanyId);
            if (companyDetailsResponse) {
              setAccountDetails(companyDetailsResponse);
            }
          }
        } catch (fetchError) {
          console.error("Error fetching updated data:", fetchError);
          toast.warning("Company added but some data may need refresh");
        }
      }
    } catch (error) {
      console.error("Error adding company:", error);
      toast.error(error.response?.data?.message || "Failed to add company");
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    // Validate before submitting
    const errors = {
      username: validateUsername(newUser.username),
      password: validatePassword(newUser.password),
      email: validateEmail(newUser.email),
      contactNo: validateContactNo(newUser.contactNo),
      accountId: validateAccountId(newUser.accountId),
      role: validateRole(newUser.roles?.[0]),
    };
    setNewUserErrors(errors);
    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) {
      toast.error("Please fix the errors before adding the user");
      return;
    }
    try {
      const response = await userService.createUser(newUser);
      if (response) {
         const verificationResponse = await authService.sendEmailVerification(newUser.email);
         
        const newUserData = response;

        setUsers((prevUsers) => {
          const updatedUsers = [...prevUsers, newUserData];
          return updatedUsers;
        });

        setCompanyUsers((prevUsers) => {
          const updatedCompanyUsers = [...prevUsers, newUserData];
          return updatedCompanyUsers;
        });

        try {
          const updatedUsersResponse = await userService.getUsersByAccount(
            newUser.accountId
          );

          if (updatedUsersResponse) {
            setUsers(updatedUsersResponse);
            setCompanyUsers(updatedUsersResponse);
          }

          setCompanyUserCounts((prevCounts) => ({
            ...prevCounts,
            [newUser.accountId]: (prevCounts[newUser.accountId] || 0) + 1,
          }));

          setShowAddUserModal(false);
          setNewUser({
            username: "",
            password: "",
            email: "",
            contactNo: "",
            accountId: "",
            roles: ["USER"],
          });
          setNewUserErrors({
            username: "",
            password: "",
            email: "",
            contactNo: "",
            accountId: "",
            role: "",
          });

          toast.success("User added successfully");

          if (selectedCompanyId === newUser.accountId) {
            const freshCompanyUsers = await userService.getUsersByAccount(
              selectedCompanyId
            );
            if (freshCompanyUsers) {
              setCompanyUsers(freshCompanyUsers);
            }
          }
        } catch (fetchError) {
          console.error("Error fetching updated data:", fetchError);
          toast.warning("User added but some data may need refresh");
        }
      }
    } catch (err) {
      console.error(
        "Registration failed:",
        err.response?.data?.message || "Registration failed"
      );
      toast.error(err.response?.data?.message || "Failed to add user");
    }
  };

  const handleCreateCredit = async () => {
    try {
      const creditAmount = parseInt(newUserData.availableCredits);
      if (isNaN(creditAmount) || creditAmount <= 0) {
        toast.error("Please enter a valid positive number");
        return;
      }

      const response = await creditService.createCredits({
        accountId: newUserData.accountId,
        credit: creditAmount,
        performedBy: userSub,
        creditType: newUserData.creditType,
      });

      if (response) {
        setShowCreateUserModal(false);
        setNewUserData({
          companyId: "",
          accountId: "",
          availableCredits: "",
          creditType: "SHORTENING",
        });
        toast.success("Credits added successfully");
      }
    } catch (error) {
      console.error("Error adding credits:", error);
      toast.error(error.response?.data?.message || "Failed to add credits");
    }
  };

  const handleUpdateCredit = async () => {
    try {
      const id = totalCredits?.content?.find(
        (item) =>
          item.creditType ===
          (updateCreditData?.creditType === "SHORTENING"
            ? "SHORTENING"
            : "TRACKING")
      )?.id;

      console.log("Updating credits with ID:", id);
      const response = await creditService.updateCredits({
        id: id,
        accountId: updateCreditData.accountId,
        transactionType: updateCreditData.transactionType,
        newCredit: parseInt(updateCreditData.newCredit),
        // active: updateCreditData.active,
        performedBy: userSub,
        creditType: updateCreditData.creditType,
      });

      if (response) {
        toast.success("Credits updated successfully");
        setShowUpdateCreditModal(false);
        setUpdateCreditData({
          accountId: "",
          transactionType: "CREDIT",
          newCredit: 0,
          active: true,
          performedBy: null,
          creditType: "SHORTENING",
        });
      }
    } catch (error) {
      console.error("Error updating credits:", error);
      toast.error(error.response?.data?.message || "Failed to update credits");
    }
  };

  const [totalCredits, setTotalCredits] = useState(null);

  const getTotalCredits = async (accountId) => {
    try {
      const response = await creditService.getCreditsByAccount(accountId);
      console.log("Total credits response:", response);
      setTotalCredits(response);
      return response;
    } catch (error) {
      console.error("Error fetching total credits:", error);
      throw error;
    }
  };

  const renderAdminDashboard = () => {
    if (!accountDetails) return null;

    return (
      <Row className="justify-content-start">
        <Col lg={8}>
          {isEditing && (
            <IoIosArrowRoundBack
              className="fs-22 mb-1"
              style={{ cursor: "pointer" }}
              onClick={() => setIsEditing(false)}
            />
          )}
          <Card>
            <Card.Body className="p-0">
              <div style={{ borderBottom: "1px solid rgb(228, 228, 228)" }}>
                {!isEditing && (
                  <h6
                    className="company-dark-bg-logo"
                    style={{ fontSize: "13px" }}
                  >
                    {accountDetails.accountName}
                  </h6>
                )}

                <div
                  className={`d-flex justify-content-between align-items-center mb-3 p-3 pt-1 pb-0 ${
                    isEditing ? " pt-3" : " pt-1"
                  }`}
                >
                  <div className="d-flex align-items-center">
                    <div className="position-relative">
                      <div
                        className="logo-container me-2"
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          border: "1px solid #E4E7EC",
                          padding: "2px",
                        }}
                      >
                        <Image
                          src={
                            logoPreview ||
                            companyLogos[accountDetails.id] ||
                            companyImage
                          }
                          alt="Company Logo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                      {isEditing && (
                        <div
                          className="position-absolute"
                          style={{
                            bottom: "-8px",
                            right: "-2px",
                            marginRight: "0.5rem",
                          }}
                        >
                          <Form.Label
                            className="mb-0"
                            style={{ cursor: "pointer" }}
                          >
                            <span
                              className="btn btn-outline-success badge bg-success"
                              style={{
                                fontSize: "8px",
                                padding: "2px 3px",
                                cursor: "pointer",
                              }}
                              title="Edit"
                            >
                              <FontAwesomeIcon
                                style={{ fontSize: "6px" }}
                                icon={faPencil}
                              />{" "}
                            </span>
                            <Form.Control
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setEditedDetails({
                                    ...editedDetails,
                                    logo: file,
                                  });
                                  setLogoPreview(URL.createObjectURL(file));
                                }
                              }}
                            />
                          </Form.Label>
                        </div>
                      )}
                    </div>
                    <div className="lh-1">
                      <div className="d-flex align-items-center">
                        <h6
                          className="mb-0 text-101828 ls-0 mb-0 me-2 text-capitalize fw-600 fs-13"
                          style={{ lineHeight: "0.5" }}
                        >
                          {accountDetails.accountName}
                        </h6>
                        <div>
                          {!isEditing && (
                            <Button
                              variant="btn btn-outline-success p-1 ps-2 pe-2"
                              size="sm"
                              style={{ fontSize: "10px" }}
                              onClick={() => {
                                setEditedDetails(accountDetails);
                                setIsEditing(true);
                              }}
                            >
                              <FontAwesomeIcon
                                className="me-1"
                                icon={faPencil}
                              />{" "}
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                      <small className="text-101828 fs-10">
                        {accountDetails.location || "No location set"}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3">
                <Row className={isEditing ? "g-3" : "g-2"}>
                  <Col md={isEditing ? 6 : 3}>
                    <div
                      className={
                        isEditing ? "details-group-unstyle" : "detail-group"
                      }
                    >
                      <small className="d-block mb-0">Account Name</small>
                      {isEditing ? (
                        <div>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={editedDetails.accountName}
                            onChange={(e) =>
                              setEditedDetails({
                                ...editedDetails,
                                accountName: e.target.value,
                              })
                            }
                            style={{
                              ...formControlStyle,
                              fontSize: "12px",
                              padding: "4px 8px",
                            }}
                            maxLength={MAX_FIELD_LEN}
                          />
                          <div className="d-flex justify-content-between mt-1">
                            <small
                              className="text-danger"
                              style={{ fontSize: "10px" }}
                            >
                              {accountNameError}
                            </small>
                            <small
                              className="text-muted"
                              style={{ fontSize: "10px" }}
                            >
                              {accountNameLen}/{MAX_FIELD_LEN}
                            </small>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          {accountDetails.accountName}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={isEditing ? 6 : 3}>
                    <div
                      className={
                        isEditing ? "details-group-unstyle" : "detail-group"
                      }
                    >
                      <small className="d-block mb-0">Language</small>
                      {isEditing ? (
                        <div>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={editedDetails.language || ""}
                            onChange={(e) =>
                              setEditedDetails({
                                ...editedDetails,
                                language: e.target.value,
                              })
                            }
                            style={{
                              ...formControlStyle,
                              fontSize: "12px",
                              padding: "4px 8px",
                            }}
                            maxLength={MAX_FIELD_LEN}
                          />
                          <div className="d-flex justify-content-end mt-1">
                            <small
                              className="text-muted"
                              style={{ fontSize: "10px" }}
                            >
                              {languageLen}/{MAX_FIELD_LEN}
                            </small>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          {accountDetails.language || "Not set"}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={isEditing ? 6 : 3}>
                    <div
                      className={
                        isEditing ? "details-group-unstyle" : "detail-group"
                      }
                    >
                      <small className="d-block mb-0">Location</small>
                      {isEditing ? (
                        <div>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={editedDetails.location || ""}
                            onChange={(e) =>
                              setEditedDetails({
                                ...editedDetails,
                                location: e.target.value,
                              })
                            }
                            style={{
                              ...formControlStyle,
                              fontSize: "12px",
                              padding: "4px 8px",
                            }}
                            maxLength={MAX_FIELD_LEN}
                          />
                          <div className="d-flex justify-content-end mt-1">
                            <small
                              className="text-muted"
                              style={{ fontSize: "10px" }}
                            >
                              {locationLen}/{MAX_FIELD_LEN}
                            </small>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          {accountDetails.location || "Not set"}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={isEditing ? 6 : 3}>
                    <div
                      className={
                        isEditing ? "details-group-unstyle" : "detail-group"
                      }
                    >
                      <small className="d-block mb-0">Status</small>
                      <div
                        style={{
                          fontSize: "12px",
                        }}
                      >
                        {isEditing ? (
                          <Form.Check
                            className="cust-form-check-switch"
                            type="switch"
                            size="sm"
                            checked={editedDetails.isActive}
                            onChange={(e) =>
                              setEditedDetails({
                                ...editedDetails,
                                isActive: e.target.checked,
                              })
                            }
                            label={
                              editedDetails.isActive ? "Active" : "Inactive"
                            }
                          />
                        ) : (
                          <span
                            className={`badge badge-before position-relative ps-3 pt-1 pe-2 pb-1 bg-${
                              accountDetails.isActive ? "success" : "danger"
                            }`}
                            style={{ fontSize: "10px" }}
                          >
                            {accountDetails.isActive ? "Active" : "Inactive"}
                          </span>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={isEditing ? 6 : 3}>
                    <div
                      className={
                        isEditing
                          ? "details-group-unstyle"
                          : "detail-group h-100"
                      }
                    >
                      <small className="d-block mb-0">Default Domain</small>
                      {isEditing ? (
                        <Form.Select
                          size="sm"
                          value={
                            editedDetails.defaultDomain ||
                            accountDetails.defaultDomain ||
                            ""
                          }
                          onChange={(e) =>
                            setEditedDetails({
                              ...editedDetails,
                              defaultDomain: e.target.value,
                            })
                          }
                          style={{
                            ...formControlStyle,
                            fontSize: "12px",
                            padding: "4px 8px",
                          }}
                        >
                          <option value="">Select Default Domain</option>
                          {domainsList.map((domain) => (
                            <option key={domain.id} value={domain.domain}>
                              {domain.domain}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <div
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          {accountDetails.defaultDomain || "Not set"}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={isEditing ? 6 : 3}>
                    <div
                      className={
                        isEditing ? "details-group-unstyle" : "detail-group"
                      }
                    >
                      <small className="d-block mb-1">URL Tracking</small>
                      <div
                        style={{ fontSize: "12px", width: "fit-content" }}
                        className="position-relative fit-content"
                      >
                        {isEditing ? (
                          userRole === "SUPERADMIN" ? (
                            <Form.Check
                              className="cust-form-check-switch"
                              type="switch"
                              size="sm"
                              checked={
                                editedDetails.urlTracking !== undefined
                                  ? editedDetails.urlTracking
                                  : accountDetails.urlTracking || false
                              }
                              onChange={(e) =>
                                setEditedDetails({
                                  ...editedDetails,
                                  urlTracking: e.target.checked,
                                })
                              }
                              label={
                                editedDetails.urlTracking !== undefined
                                  ? editedDetails.urlTracking
                                    ? "Enabled"
                                    : "Disabled"
                                  : accountDetails.urlTracking
                                  ? "Enabled"
                                  : "Disabled"
                              }
                            />
                          ) : (
                            <div>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip className="fs-12">
                                    {accountDetails.urlTracking
                                      ? "Only Administrator has access to this"
                                      : "This Feature is currently disabled. Please contact the system administrator"}
                                  </Tooltip>
                                }
                              >
                                <div>
                                  <Form.Check
                                    className="cust-form-check-switch"
                                    type="switch"
                                    size="sm"
                                    checked={
                                      accountDetails.urlTracking || false
                                    }
                                    disabled
                                    style={{ cursor: "not-allowed" }}
                                  />
                                </div>
                              </OverlayTrigger>
                            </div>
                          )
                        ) : (
                          <span
                            className={`badge bg-${
                              accountDetails.urlTracking
                                ? "success"
                                : "secondary"
                            }`}
                            style={{ fontSize: "10px", borderRadius: "32px" }}
                          >
                            {accountDetails.urlTracking
                              ? "Enabled"
                              : "Disabled"}
                          </span>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={isEditing ? 6 : 3}>
                    <div
                      className={
                        isEditing ? "details-group-unstyle" : "detail-group"
                      }
                    >
                      <small className="d-block mb-1">
                        Individual Tracking
                      </small>
                      <div
                        style={{ fontSize: "12px", width: "fit-content" }}
                        className="position-relative fit-content"
                      >
                        {isEditing ? (
                          userRole === "SUPERADMIN" ? (
                            <Form.Check
                              className="cust-form-check-switch"
                              type="switch"
                              size="sm"
                              checked={
                                editedDetails.individualTracking !== undefined
                                  ? editedDetails.individualTracking
                                  : accountDetails.individualTracking || false
                              }
                              onChange={(e) =>
                                setEditedDetails({
                                  ...editedDetails,
                                  individualTracking: e.target.checked,
                                })
                              }
                              label={
                                editedDetails.individualTracking !== undefined
                                  ? editedDetails.individualTracking
                                    ? "Enabled"
                                    : "Disabled"
                                  : accountDetails.individualTracking
                                  ? "Enabled"
                                  : "Disabled"
                              }
                            />
                          ) : (
                            <div>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip className="fs-12">
                                    {accountDetails.individualTracking
                                      ? "Only Administrator has access to this"
                                      : "This Feature is currently disabled. Please contact the system administrator"}
                                  </Tooltip>
                                }
                              >
                                <div>
                                  <Form.Check
                                    className="cust-form-check-switch"
                                    type="switch"
                                    size="sm"
                                    checked={
                                      accountDetails.individualTracking || false
                                    }
                                    disabled
                                    style={{ cursor: "not-allowed" }}
                                  />
                                </div>
                              </OverlayTrigger>
                            </div>
                          )
                        ) : (
                          <span
                            className={`badge bg-${
                              accountDetails.individualTracking
                                ? "success"
                                : "secondary"
                            }`}
                            style={{ fontSize: "10px", borderRadius: "32px" }}
                          >
                            {accountDetails.individualTracking
                              ? "Enabled"
                              : "Disabled"}
                          </span>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col>
                    {isEditing && (
                      <div className="mt-3 d-flex justify-content-end">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          style={{
                            fontSize: "12px",
                            padding: "4px 16px",
                            borderRadius: "8px",
                          }}
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          style={{ fontSize: "12px", padding: "4px 16px" }}
                          onClick={handleUpdate}
                          disabled={isFormInvalid}
                        >
                          Save
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
          <Card className="card-nostyle">
            <Card.Body className="p-0">
              {!isEditing && ( // Conditional rendering for the Tabs component
                <div className="p-0 pt-3">
                  <Tabs
                    defaultActiveKey="Domains"
                    id="uncontrolled-tab-example"
                    className="mb-3 bordered-tab"
                  >
                    <Tab eventKey="Domains" title="Domains">
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="fw-500 fs-13">Domains</small>

                          <Button
                            variant="outline-success"
                            size="sm"
                            style={{
                              fontSize: "11px",
                              padding: "4px 6px",
                              borderRadius: "6px",
                            }}
                            onClick={() => setShowDomainModal(true)}
                          >
                            + Add
                          </Button>
                        </div>
                        <div className="mt-3">
                          <div className="table_responsive w-100">
                            <Table responsive className="w-100 mb-0">
                              <thead className="table-success custom-thead">
                                <tr>
                                  <th style={{ textTransform: "capitalize" }}>
                                    Domains
                                  </th>
                                  <th style={{ textTransform: "capitalize" }}>
                                    Created At
                                  </th>
                                  <th style={{ textTransform: "capitalize" }}>
                                    Status
                                  </th>
                                  <th
                                    className="text-end"
                                    style={{ textTransform: "capitalize" }}
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {domainsList.map((domainObj) => (
                                  <tr key={domainObj.id}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <span className="fs-10">
                                          {domainObj.domain}
                                        </span>
                                        {domainObj.defaultDomain && (
                                          <span
                                            className="badge bg-success ms-2 badge-before position-relative ps-3"
                                            style={{ fontSize: "10px" }}
                                          >
                                            Default
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className="fs-10">
                                        {domainObj.createdAt
                                          ? new Date(
                                              domainObj.createdAt
                                            ).toLocaleString()
                                          : "N/A"}
                                      </span>
                                    </td>
                                    <td>
                                      <span
                                        className={`badge bg-${
                                          domainObj.assigned
                                            ? "success"
                                            : "danger"
                                        }`}
                                        style={{
                                          fontSize: "10px",
                                          borderRadius: "32px",
                                        }}
                                      >
                                        {domainObj.assigned
                                          ? "Active"
                                          : "Inactive"}
                                      </span>
                                      <span className={`badge bg-${domainObj.assigned ? 'success' : 'danger'}`} style={{ fontSize: "10px", borderRadius:'32px' }}>
                                        {domainObj.assigned ? 'Verified' : 'Not Verified'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="d-flex flex-row-reverse align-items-center gap-2">
                                        <Dropdown>
                                          <Dropdown.Toggle
                                            variant="outline-secondary"
                                            size="sm"
                                            className="fs-10 p-2 pt-1 pb-1 border-0"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <FaEllipsisV />
                                          </Dropdown.Toggle>
                                          <Dropdown.Menu>
                                            {!domainObj.defaultDomain && (
                                              <Dropdown.Item
                                                href="#"
                                                onClick={() =>
                                                  handleSetDefaultDomain(
                                                    domainObj
                                                  )
                                                }
                                                className="fs-12 pt-2 pb-2"
                                              >
                                                <FaStar className="align-middle fs-12 me-1" />
                                                Set as Default
                                              </Dropdown.Item>
                                            )}
                                            {userRole === "SUPERADMIN" && (
                                              <Dropdown.Item
                                                href="#"
                                                onClick={() => handleVerifyDomainNow(domainObj)}
                                                className="fs-12 pt-2 pb-2"
                                              >
                                                Verify Now
                                              </Dropdown.Item>
                                            )}
                                            <Dropdown.Item
                                              className="fs-12 pt-2 pb-2"
                                              onClick={() => handleDeleteClick(domainObj)}
                                            >
                                              <FaTrash className="align-middle fs-12 me-1" />
                                              Delete
                                            </Dropdown.Item>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    </Tab>
                    <Tab eventKey="Sender IDs" title="Sender IDs">
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="fw-500 fs-13">Sender IDs</small>

                          <Button
                            variant="outline-success"
                            size="sm"
                            style={{
                              fontSize: "11px",
                              padding: "4px 6px",
                              borderRadius: "6px",
                            }}
                            onClick={() => setShowSenderIdModal(true)}
                          >
                            + Add
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="table_responsive w-100">
                          <Table responsive className="w-100 mb-0">
                            <thead className="table-success custom-thead">
                              <tr>
                                <th style={{ textTransform: "capitalize" }}>
                                  Sender ID
                                </th>
                                <th style={{ textTransform: "capitalize" }}>
                                  Created At
                                </th>
                                <th
                                  className="text-end"
                                  style={{ textTransform: "capitalize" }}
                                >
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {senderIdsList.map((senderIdObj) => (
                                <tr key={senderIdObj.id}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <span className="fs-10">
                                        {senderIdObj.senderId}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="fs-10">
                                      {senderIdObj.createdAt
                                        ? new Date(
                                            senderIdObj.createdAt
                                          ).toLocaleString()
                                        : "N/A"}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="d-flex flex-row-reverse align-items-center gap-2">
                                      <Button
                                        variant="outline-danger"
                                        className="fs-10 p-2 pt-1 btn-outline-danger pb-1"
                                        style={{ fontSize: "11px" }}
                                        onClick={() =>
                                          handleSenderIdDeleteClick(senderIdObj)
                                        }
                                        title="Delete"
                                      >
                                        <FaTrash className="" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Tab>
                  </Tabs>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        {domainModal}
        {senderIdModal}
        {deleteConfirmationModal}

        <Col lg={4} md={6}>
          <>
            <style>
              {`
                      .price-feature-list li
                        { font-weight: '400', font-size:'12px'}
                      .price-feature-list { background: #F5F5F5;}
                       pricing-cards h4 { font-size: 1.2rem !important}
                      `}
                      </style>
                    </>
                      <Card className="pricing-cards">
                        <Card.Header className="bg-white text-center border-0 pt-4 pb-0 ps-5 pe-5">
                          <h5 className="fw-600">Enterprise</h5>
                          
                          <p className="fw-400 fs-12 mb-4" style={{color:'#A6A6A6'}}>Unveil new superpowers and join the Design Leaque</p>
                          <div className="price-value"><h2 class="fw-600 mb-0 fs-28 position-relative m-auto" style={{width:'fit-content'}}><span className='dollarSign'>$</span>#</h2>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <ul className="list-unstyled mb-0 price-feature-list">
                            <li className="d-flex fs-12 align-items-center mb-3">
                            <div className='checkIcon'><FontAwesomeIcon icon={faCheck} /></div>
                            All Business features
                            </li>
                            <li className="d-flex fs-12 align-items-center mb-3">
                            <div className='checkIcon'><FontAwesomeIcon icon={faCheck} /></div>
                            Account success Manager
                            </li>
                            <li className="d-flex fs-12 align-items-center mb-3">
                            <div className='checkIcon'><FontAwesomeIcon icon={faCheck} /></div>
                            Single Sign-On (SSO)
                            </li>
                            <li className="d-flex fs-12 align-items-center mb-3">
                            <div className='checkIcon'><FontAwesomeIcon icon={faCheck} /></div>
                            Co-conception pogram
                            </li>
                            <li className="d-flex fs-12 align-items-center mb-3">
                            <div className='checkIcon'><FontAwesomeIcon icon={faCheck} /></div>
                            Collaboration-Soon
                            </li>
                            <Button variant="success-outline-btn" className="w-100">Contact Us</Button>
                          </ul>
        
                        </Card.Body>
                      </Card>
                    </Col>

      </Row>
    );
  };

  const domainModal = (
    <Modal
      show={showDomainModal}
      onHide={() => setShowDomainModal(false)}
      centered
    >
      <Modal.Header
        className="bg-white"
        closeButton
        style={{ borderBottom: "1px solid #E4E7EC" }}
      >
        <Modal.Title style={{ fontSize: "18px", fontWeight: "600" }}>
          Add New Domain
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Form.Group>
          <Form.Label style={labelStyle}>Domain Name</Form.Label>
          <div className="d-flex">
            <Form.Select
              className="w-auto me-2"
              value={selectedProtocol}
              onChange={(e) => setSelectedProtocol(e.target.value)}
              style={{ minWidth: "100px", ...formControlStyle }}
            >
              <option value="http://">http://</option>
              <option value="https://">https://</option>
            </Form.Select>
            <Form.Control
              type="text"
              placeholder="Enter domain (e.g., example.com)"
              value={newDomain}
              onChange={(e) => {
                const value = e.target.value.replace(/^(https?:\/\/)/, "");
                setNewDomain(value);
                if (domainError) setDomainError("");
              }}
              maxLength={43}
              isInvalid={!!domainError}
              style={formControlStyle}
            />
            {domainError ? (
              <div
                className="text-danger mt-1"
                style={{ fontSize: "0.875rem" }}
              >
                {domainError}
              </div>
            ) : (
              <Form.Text className="text-muted">
                Max 50 characters total (including http:// or https://)
              </Form.Text>
            )}
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid #E4E7EC" }}>
        <Button
          variant="outline-secondary"
          onClick={() => setShowDomainModal(false)}
          style={{ borderRadius: "8px" }}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleAddDomain}
          style={{ borderRadius: "8px" }}
          className="br-radius-8"
        >
          Add Domain
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const senderIdModal = (
    <Modal
      show={showSenderIdModal}
      onHide={() => setShowSenderIdModal(false)}
      centered
    >
      <Modal.Header closeButton className="bg-transparent">
        <Modal.Title>Add New Sender ID</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>Sender ID</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Sender ID (e.g., ABCDEF)"
            value={newSenderId}
            onChange={(e) => {
              setNewSenderId(e.target.value);
              if (senderIdError) setSenderIdError("");
            }}
            maxLength={20}
            isInvalid={!!senderIdError}
          />
          {senderIdError ? (
            <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
              {senderIdError}
            </div>
          ) : (
            <Form.Text className="text-muted">
              3-20 characters, letters and numbers only (no symbols)
            </Form.Text>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowSenderIdModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleAddSenderId}>
          Add Sender ID
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const createCompanyModal = (
    <Modal
      show={showCreateCompanyModal}
      onHide={() => setShowCreateCompanyModal(false)}
      centered
      size="md"
    >
      <Modal.Header closeButton className="border-0 bg-white">
        <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
          Add Acoount
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4 pt-0">
        {/* {Object.values(newCompanyErrors).some((e) => !!e) && (
          <Alert variant="warning" className="mb-3">
            <div className="fw-600 mb-2">Please fix the following:</div>
            <ul className="mb-0 ps-3">
              {newCompanyErrors.accountName && <li>{newCompanyErrors.accountName}</li>}
              {newCompanyErrors.domains && <li>{newCompanyErrors.domains}</li>}
              {newCompanyErrors.defaultDomain && <li>{newCompanyErrors.defaultDomain}</li>}
              {newCompanyErrors.senderIds && <li>{newCompanyErrors.senderIds}</li>}
            </ul>
          </Alert>
        )} */}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
              Account Name
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter company name"
              value={newCompanyData.accountName}
              onChange={(e) => {
                const val = e.target.value;
                setNewCompanyData({
                  ...newCompanyData,
                  accountName: val,
                });
                setNewCompanyErrors((prev) => ({
                  ...prev,
                  accountName: validateAccountName(val),
                }));
              }}
              isInvalid={!!newCompanyErrors.accountName}
            />
            <Form.Control.Feedback type="invalid">
              {newCompanyErrors.accountName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
              Domains (comma-separated)
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter domains (e.g., https://example1.com,https://example2.com)"
              value={
                Array.isArray(newCompanyData.domains)
                  ? newCompanyData.domains.join(",")
                  : newCompanyData.domains
              }
              onChange={(e) => {
                const inputValue = e.target.value;
                const domainsArray = inputValue
                  ? inputValue.split(",").map((domain) => domain.trim())
                  : [];
                setNewCompanyData({
                  ...newCompanyData,
                  domains: domainsArray,
                });
                setNewCompanyErrors((prev) => ({
                  ...prev,
                  domains: validateDomains(domainsArray),
                  defaultDomain: validateDefaultDomain(
                    newCompanyData.defaultDomain,
                    domainsArray
                  ),
                }));
              }}
              isInvalid={!!newCompanyErrors.domains}
            />
            <Form.Control.Feedback type="invalid">
              {newCompanyErrors.domains}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
              Default Domain
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Default domain"
              value={newCompanyData.defaultDomain}
              onChange={(e) =>
                setNewCompanyData({
                  ...newCompanyData,
                  defaultDomain: e.target.value,
                })
              }
              onBlur={() =>
                setNewCompanyErrors((prev) => ({
                  ...prev,
                  defaultDomain: validateDefaultDomain(
                    newCompanyData.defaultDomain,
                    newCompanyData.domains
                  ),
                }))
              }
              isInvalid={!!newCompanyErrors.defaultDomain}
            />
            <Form.Control.Feedback type="invalid">
              {newCompanyErrors.defaultDomain}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
              Sender IDs (comma-separated)
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter sender IDs (e.g., PARK123)"
              value={
                Array.isArray(newCompanyData.senderIds)
                  ? newCompanyData.senderIds.join(",")
                  : ""
              }
              onChange={(e) => {
                const inputValue = e.target.value;
                const senderIdsArray = inputValue
                  ? inputValue.split(",").map((id) => id.trim())
                  : [];
                setNewCompanyData({
                  ...newCompanyData,
                  senderIds: senderIdsArray,
                });
                setNewCompanyErrors((prev) => ({
                  ...prev,
                  senderIds: validateSenderIds(senderIdsArray),
                }));
              }}
              isInvalid={!!newCompanyErrors.senderIds}
            />
            <Form.Control.Feedback type="invalid">
              {newCompanyErrors.senderIds}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
              Status
            </Form.Label>
            <Form.Select
              value={newCompanyData.isActive}
              onChange={(e) =>
                setNewCompanyData({
                  ...newCompanyData,
                  isActive: e.target.value === "true",
                })
              }
            >
              <option value={true}>Active</option>
              <option value={false}>Inactive</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 p-3">
        <div className="d-flex gap-3 w-75">
          <Button
            variant="danger"
            className="flex-grow-1 br-radius-8"
            onClick={() => setShowCreateCompanyModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            className="flex-grow-1 br-radius-8"
            onClick={handleCreateCompany}
            disabled={
              !newCompanyData.accountName ||
              !newCompanyData.domains.length ||
              !newCompanyData.defaultDomain ||
              Object.values(newCompanyErrors).some((e) => !!e)
            }
          >
            Add Company
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );

  const addUserModal = (
    <Modal
      show={showAddUserModal}
      onHide={() => {
        setShowAddUserModal(false);
        setNewUser({
          username: "",
          password: "",
          email: "",
          contactNo: "",
          accountId: "",
          roles: ["USER"],
        });
        setNewUserErrors({
          username: "",
          password: "",
          email: "",
          contactNo: "",
          accountId: "",
          role: "",
        });
      }}
      centered
      size="md"
    >
      <Modal.Header closeButton className="border-0 bg-white">
        <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
          Add New User
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {/* {Object.values(newUserErrors).some(Boolean) && (
          <Alert variant="warning" className="mb-3">
            <div className="mb-1">Please resolve the following:</div>
            <ul className="mb-0">
              {Object.entries(newUserErrors).map(([key, msg]) =>
                msg ? <li key={key}>{msg}</li> : null
              )}
            </ul>
          </Alert>
        )} */}
        <Form onSubmit={handleAddUser} noValidate>
          <Row>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Username
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter username"
                  value={newUser.username}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewUser({ ...newUser, username: value });
                    setNewUserErrors((prev) => ({
                      ...prev,
                      username: validateUsername(value),
                    }));
                  }}
                  onBlur={(e) =>
                    setNewUserErrors((prev) => ({
                      ...prev,
                      username: validateUsername(e.target.value),
                    }))
                  }
                  className="br-radius-8"
                  isInvalid={!!newUserErrors.username}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {newUserErrors.username}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Password
                </Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showNewUserPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={newUser.password}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewUser({ ...newUser, password: value });
                      setNewUserErrors((prev) => ({
                        ...prev,
                        password: validatePassword(value),
                      }));
                    }}
                    onBlur={(e) =>
                      setNewUserErrors((prev) => ({
                        ...prev,
                        password: validatePassword(e.target.value),
                      }))
                    }
                    className={`br-radius-8 ${
                      newUserErrors.password ? "is-invalid" : ""
                    }`}
                    style={{
                      paddingRight: newUserErrors.password ? "60px" : "40px",
                    }}
                    isInvalid={!!newUserErrors.password}
                    required
                  />
                  <span
                    className="position-absolute top-50 translate-middle-y fs-12 text-success"
                    style={{
                      right: newUserErrors.password ? "30px" : "10px",
                      cursor: "pointer",
                      zIndex: 10,
                    }}
                    onClick={() => setShowNewUserPassword((prev) => !prev)}
                    aria-label={
                      showNewUserPassword ? "Hide password" : "Show password"
                    }
                  >
                    <FontAwesomeIcon
                      icon={showNewUserPassword ? faEyeSlash : faEye}
                    />
                  </span>
                </div>
                {newUserErrors.password && (
                  <div className="invalid-feedback d-block">
                    {newUserErrors.password}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={newUser.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewUser({ ...newUser, email: value });
                    setNewUserErrors((prev) => ({
                      ...prev,
                      email: validateEmail(value),
                    }));
                  }}
                  onBlur={(e) =>
                    setNewUserErrors((prev) => ({
                      ...prev,
                      email: validateEmail(e.target.value),
                    }))
                  }
                  className="br-radius-8"
                  isInvalid={!!newUserErrors.email}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {newUserErrors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Contact Number
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter contact number"
                  value={newUser.contactNo}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewUser({ ...newUser, contactNo: value });
                    setNewUserErrors((prev) => ({
                      ...prev,
                      contactNo: validateContactNo(value),
                    }));
                  }}
                  onBlur={(e) =>
                    setNewUserErrors((prev) => ({
                      ...prev,
                      contactNo: validateContactNo(e.target.value),
                    }))
                  }
                  className="br-radius-8"
                  isInvalid={!!newUserErrors.contactNo}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {newUserErrors.contactNo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Company
                </Form.Label>
                <Form.Select
                  className="br-radius-8"
                  value={newUser.accountId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewUser({ ...newUser, accountId: value });
                    setNewUserErrors((prev) => ({
                      ...prev,
                      accountId: validateAccountId(value),
                    }));
                  }}
                  onBlur={(e) =>
                    setNewUserErrors((prev) => ({
                      ...prev,
                      accountId: validateAccountId(e.target.value),
                    }))
                  }
                  isInvalid={!!newUserErrors.accountId}
                  required
                >
                  <option value="">Select the Company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.accountName}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {newUserErrors.accountId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Role
                </Form.Label>
                <Form.Select
                  className="br-radius-8"
                  value={newUser.roles[0]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewUser({ ...newUser, roles: [value] });
                    setNewUserErrors((prev) => ({
                      ...prev,
                      role: validateRole(value),
                    }));
                  }}
                  onBlur={(e) =>
                    setNewUserErrors((prev) => ({
                      ...prev,
                      role: validateRole(e.target.value),
                    }))
                  }
                  isInvalid={!!newUserErrors.role}
                  required
                >
                  <option value="">Select Role</option>
                  {["USER", "ADMIN"].map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {newUserErrors.role}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-top p-3">
        <div className="d-flex gap-3 w-75">
          <Button
            variant="light bg-white"
            className="flex-grow-1 br-radius-8"
            onClick={() => setShowAddUserModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            className="flex-grow-1 br-radius-8"
            onClick={handleAddUser}
            disabled={
              Object.values(newUserErrors).some(Boolean) ||
              !newUser.username ||
              !newUser.password ||
              !newUser.email ||
              !newUser.contactNo ||
              !newUser.accountId ||
              !newUser.roles?.[0]
            }
          >
            Add User
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );

  const deleteConfirmationModal = (
    <Modal
      show={showDeleteConfirmationModal}
      onHide={() => {
        setShowDeleteConfirmationModal(false);
        setItemToDelete(null);
      }}
      centered
      size="md"
    >
      <Modal.Header closeButton className="border-0 bg-white">
        <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
          Confirm Delete
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center p-4">
        <p className="mb-0">
          Are you sure you want to delete this {itemToDelete?.type}?
          <br />
          <span className="fw-500">{itemToDelete?.name}</span>
        </p>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 p-3">
        <div className="d-flex gap-3 w-75">
          <Button
            variant="light"
            className="flex-grow-1 br-radius-8"
            onClick={() => {
              setShowDeleteConfirmationModal(false);
              setItemToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="flex-grow-1 br-radius-8"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );

  const renderTabContent = () => {
    if (isLoading) {
      return <div className="text-center p-4">Loading...</div>;
    }

    if (userRole === "ADMIN") {
      return renderAdminDashboard();
    }

    switch (activeTab) {
      case "account-dashboard":
        return <Analytics selectedCompanyId={selectedCompanyId} />;
      case "account-details":
        return (
          <CompanyManagement
            embedded={true}
            selectedCompanyId={selectedCompanyId}
          />
        );
      case "user-management":
        return (
          <UserManagement
            embedded={true}
            selectedCompanyId={selectedCompanyId}
          />
        );
      case "credit-management":
        return (
          <CreditDashboard
            embedded={true}
            selectedCompanyId={selectedCompanyId}
          />
        );
      default:
        return null;
    }
  };

  // const handleFullScreen = () => {
  //   if (tabContainerRef.current) {
  //     if (tabContainerRef.current.requestFullscreen) {
  //       tabContainerRef.current.requestFullscreen();
  //     } else if (tabContainerRef.current.webkitRequestFullscreen) {
  //       tabContainerRef.current.webkitRequestFullscreen();
  //     } else if (tabContainerRef.current.msRequestFullscreen) {
  //       tabContainerRef.current.msRequestFullscreen();
  //     }
  //   }
  // };

  return (
    <>
      <Container
        fluid
        className="ps-0 pb-4 pe-0 overflow-hidden user-management-container bg-white"
      >
        <Card className="card-nostyle tab-card ">
          <Card.Body className="ps-0 pe-0">
            <div className="custom-domain-containers">
              <div
                className="d-flex justify-content-between ps-4 pe-4 pt-0 pb-3 align-items-center"
                style={{ borderBottom: "1px solid #EAECF0" }}
              >
                <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 pb-0 fs-20 poppins fw-600">
                  <Image
                    src={creditManagementIcon}
                    className="me-1"
                    width="20"
                  />{" "}
                  Account Management
                </h4>
                {activeTab === "user-management" ? (
                  <Button
                    variant="success"
                    className="br-radius-8 d-flex align-items-center gap-2"
                    onClick={() => {
                      setShowAddUserModal(true);
                      const userAccountId = jwtDecode(
                        localStorage.getItem("token")
                      ).accountId;
                      if (userAccountId) {
                        setNewUser((prev) => ({
                          ...prev,
                          accountId: userAccountId,
                        }));
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="fs-12" />
                    <span className="fs-12">Add User</span>
                  </Button>
                ) : activeTab === "credit-management" ? (
                  <div className="d-flex gap-2">
                    {/* <Button
                      variant="success"
                      className="br-radius-8 d-flex align-items-center gap-2"
                      onClick={() => setShowCreateUserModal(true)}
                      style={{ padding: "8px 16px" }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="fs-12" />
                      <span className="fs-12">Add Credit</span>
                    </Button> */}
                    <Button
                      variant="primary"
                      className="br-radius-8 d-flex align-items-center gap-2"
                      onClick={() => setShowUpdateCreditModal(true)}
                      style={{ padding: "8px 16px" }}
                    >
                      <span className="fs-12">Add/Update Credit</span>
                    </Button>
                  </div>
                ) : (
                  (activeTab === "account-details" ||
                    (!isSuperAdmin &&
                      userRole !== "ADMIN" &&
                      activeTab !== "account-dashboard")) && (
                    <Button
                      variant="success"
                      className="br-radius-8 d-flex align-items-center gap-2"
                      onClick={() => setShowCreateCompanyModal(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} className="fs-12" />
                      <span className="fs-12">Add Account</span>
                    </Button>
                  )
                )}
              </div>
            </div>
          </Card.Body>
        </Card>

        <div className="ps-4 pe-4">
          {/* <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <h4 className="mb-0">Account Management</h4>
          </div>
          <Button variant="success" className="add-account-btn">
            + Add Account
          </Button>
        </div> */}

          {userRole === "ADMIN" ? (
            <div className="mt-2">{renderTabContent()}</div>
          ) : (
            <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
              <Nav variant="tabs" className="mb-4 cust-tab border-bottom">
                <Nav.Item>
                  <Nav.Link eventKey="account-dashboard">
                    Account Dashboard
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="account-details">
                    Account Details
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="user-management">
                    User Management
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="credit-management">
                    Credit Management
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Row>
                <Col lg={4}>
                  <CompanySidebar
                    companies={companies}
                    selectedCompanyId={selectedCompanyId}
                    onSelectCompany={(id) => setSelectedCompanyId(id)}
                    searchTerm={companySearchTerm}
                    onSearchTermChange={setCompanySearchTerm}
                    companyLogos={companyLogos}
                  />
                </Col>
                <Col lg={8}>
                  <div ref={tabContainerRef}>
                    <Tab.Content className="tab-body-content">
                      <Tab.Pane eventKey={activeTab} active>
                        {renderTabContent()}
                      </Tab.Pane>
                    </Tab.Content>
                  </div>
                </Col>
              </Row>
            </Tab.Container>
          )}
        </div>
      </Container>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {addUserModal}
      {createCompanyModal}
      <Modal
        show={showCreateUserModal}
        onHide={() => setShowCreateUserModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Add Credits
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Company
              </Form.Label>
              <Form.Select
                className="rounded-pill"
                value={newUserData.accountId}
                onChange={(e) =>
                  setNewUserData({
                    ...newUserData,
                    accountId: e.target.value,
                    companyId: e.target.value,
                  })
                }
                required
              >
                <option value="">Select the Company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.accountName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Credit Type
              </Form.Label>
              <Form.Select
                className="rounded-pill custom-select-green-option"
                value={newUserData.creditType}
                onChange={(e) =>
                  setNewUserData({
                    ...newUserData,
                    creditType: e.target.value,
                  })
                }
                required
              >
                <option value="SHORTENING">SHORTENING</option>
                <option value="TRACKING">TRACKING</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Credits
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter credits"
                value={newUserData.availableCredits}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    setNewUserData({ ...newUserData, availableCredits: value });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === ".") {
                    e.preventDefault();
                  }
                }}
                min="0"
                step="1"
                className="rounded-pill"
                style={{ color: "#000" }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="danger"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowCreateUserModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={handleCreateCredit}
              disabled={!newUserData.availableCredits || !newUserData.accountId}
            >
              Add Credit
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showUpdateCreditModal}
        onHide={() => setShowUpdateCreditModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Update Credits
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Company
              </Form.Label>
              <Form.Select
                className="rounded-pill"
                value={updateCreditData.accountId}
                onChange={async (e) => {
                  e.preventDefault();
                  const accountId = e.target.value;
                  setUpdateCreditData({
                    ...updateCreditData,
                    accountId: e.target.value,
                  });
                  const credits = await getTotalCredits(accountId);
                }}
                required
              >
                <option value="">Select the Company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.accountName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Transaction Type
              </Form.Label>
              <Form.Select
                className="rounded-pill custom-select-green-option"
                value={updateCreditData.transactionType}
                onChange={(e) =>
                  setUpdateCreditData({
                    ...updateCreditData,
                    transactionType: e.target.value,
                  })
                }
                required
              >
                <option value="CREDIT">CREDIT</option>
                <option value="DEBIT">DEBIT</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Credits
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter credits"
                value={updateCreditData.newCredit}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    setUpdateCreditData({
                      ...updateCreditData,
                      newCredit: value,
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === ".") {
                    e.preventDefault();
                  }
                }}
                min="0"
                step="1"
                className="rounded-pill"
                style={{ color: "#000" }}
              />
            </Form.Group>

            <Form.Group className="mb-3 d-none">
              <Form.Check
                type="switch"
                id="activeSwitch"
                label="Active"
                checked={updateCreditData.active}
                onChange={(e) =>
                  setUpdateCreditData({
                    ...updateCreditData,
                    active: e.target.checked,
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Credit Type
              </Form.Label>
              <Form.Select
                className="rounded-pill custom-select-green-option"
                value={updateCreditData.creditType}
                onChange={(e) =>
                  setUpdateCreditData({
                    ...updateCreditData,
                    creditType: e.target.value,
                  })
                }
                required
              >
                <option value="SHORTENING">SHORTENING</option>
                <option value="TRACKING">TRACKING</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="danger"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowUpdateCreditModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={handleUpdateCredit}
              disabled={
                !updateCreditData.newCredit || !updateCreditData.accountId
              }
            >
              Update Credit
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AccountManagement;
